import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { Location } from '../../../common/react/Location';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ProjectDefinition } from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo, SpaceItemForNonEntitySpace, SpaceType } from '../../state/SpaceInfo';
import { ExplorerEmptyStateProps } from '../Components/ExplorerEmpyState';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '../Explorer/FilterByTypeDropdown';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedFavoritesFilterByTypeMenu } from './ConnectedFavoritesFilterByTypeMenu';

export interface FavoritesSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly urls: SpacesSiteUrls;
}

export interface FavoritesSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface FavoritesSpaceOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface FavoritesSpaceAngularProps {
  readonly telemetry: Telemetry;
  readonly browserTitleService: BrowserTitleService;
}

export type FavoritesSpaceProps = FavoritesSpaceStateProps
  & FavoritesSpaceDispatchProps
  & FavoritesSpaceOwnProps
  & FavoritesSpaceAngularProps;

const FavoritesSpaceInfo: SpaceInfo = {
  item: SpaceItemForNonEntitySpace,
  spaceType: 'favorites',
  subspaceName: undefined
};

export class FavoritesSpace extends React.PureComponent<FavoritesSpaceProps> {
  public componentDidMount() {
    this.props.requestSpaceContents();
    this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.favoritesBrowserTitle() });
  }

  public componentDidUpdate(prevProps: FavoritesSpaceProps) {
    if(!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }

  public render(): JSX.Element {
    return(
      <ConnectedSpaceInfoProvider spaceInfo={FavoritesSpaceInfo}>
        <ConnectedQueryParamUpdateProvider
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
          shouldUpdateBreadcrumbs={false}
          shouldUpdateContentTypes={true}
          shouldUpdateFilters={true}
          shouldUpdateSort={true}
        >
          <div className={SpaceContainerStyles.SpaceContainer}>
            <div className={SpaceContainerStyles.MastheadArea}>
              <div className={ExplorerStyles.SpaceHeaderContent}>
                <div className={ExplorerStyles.SpaceHeaderText}>{Messages.favoritesBrowserTitle()}</div>
              </div>
              <div style={SpaceContainerStyles.MastheadSubheadingArea}>
                {FeatureFlags.reorderFavoritesEnabled && Messages.favoritesMastheadSubheading()}
              </div>
            </div>
            <div className={SpaceContainerStyles.SubplaceArea}>
              <ConnectedExplorer
                angularContext={this.props.angularContext}
                supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
                telemetry={this.props.telemetry}
                emptyStateProps={this.emptyStateProps()}
                toolbarProps={{
                  supportFilterPanel: true,
                  toolbarContentFilterControl: this.contentFilterControl(),
                  toolbarVisibility: 'enabled'
                }}
              />
            </div>
          </div>
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
        {!FeatureFlags.contentTypeMenuUpdate &&
          <FilterByTypeDropdown
            filterSelected={this.props.filterSelected}
            contentFilter={this.props.contentTypeFilterValues}
            defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
            menuPanelClass={ConnectedFavoritesFilterByTypeMenu}
            clearContentCounts={this.props.clearContentCounts}
            spaceType={FavoritesSpaceInfo.spaceType}
          />}
        {FeatureFlags.contentTypeMenuUpdate &&
          <FilterByTypeDropdown
            filterSelected={this.props.filterSelected}
            contentFilter={this.props.contentTypeFilterValues}
            defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
            clearContentCounts={this.props.clearContentCounts}
            spaceType={FavoritesSpaceInfo.spaceType}
          />}
      </React.Fragment>
    );
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      iconUrl: imgPath('spaces_empty_favorite.svg'),
      primaryMessage: Messages.favoritesEmptyStatePrimary(),
      secondaryMessage: Messages.favoritesEmptyStateSecondary(),
      onActionButton: this.onNoContentAction,
      actionButtonMessage: Messages.emptyStateCallToAction()
    };
  }

  private onNoContentAction = (): void => {
    Location.instance.setUrl(this.props.urls.exploreUrl());
  }
}

const FavoritesSpaceAngularProvider =
  angularProviderHOC<FavoritesSpaceProps, FavoritesSpaceAngularProps, FavoritesSpaceAngularProps>({
    getAngularServicesWithServiceLocator: () => ({
      telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
      browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService')
    }),
    selectPropsFromAngularServices: angularServices => angularServices,
  });

export const AngularWrappedFavoritesSpace = FavoritesSpaceAngularProvider(FavoritesSpace);


